import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Sparkles, ShieldCheck, Landmark, ScrollText, Search, Building2,
  CheckCircle2, Languages, Calculator, Lock, DatabaseBackup, KeyRound,
  History, Trash2, Server, FileCheck2, GitBranch, Factory, Wrench, CalendarClock, Layers, BadgeIndianRupee,
} from "lucide-react";
import { Button } from "@/components/ui/button";

// ─── Product tour screenshots (real app captures under /screenshots/) ────────
const TOUR = [
  { key: "mis-pack",            title: "10-Section MIS Pack",             kicker: "// Board-ready insights",
    desc: "EBITDA · Segment P&L · Top customers/products · Cash · Unusual transactions · Compliance status — one downloadable Excel/PDF, boardroom-ready in 30 seconds.",
    img: "/screenshots/mis-pack.jpg" },
  { key: "consolidation",       title: "Group Consolidation (AS-21)",     kicker: "// Multi-entity",
    desc: "Statutory AS-21 line-added consolidation with intercompany elimination and minority-interest carve-out. Toggle between Statutory and MIS views. Any depth of hierarchy.",
    img: "/screenshots/consolidation.jpg" },
  { key: "advance-tax",         title: "Advance Tax & MAT Calculator",    kicker: "// Direct Tax",
    desc: "Rolling FY forecast with editable projections. Normal Provisions vs MAT (Sec 115JB) side-by-side, plus a Sec 211 quarterly instalment schedule — no more spreadsheet gymnastics.",
    img: "/screenshots/advance-tax.jpg" },
  { key: "compliance-calendar", title: "Live Compliance Calendar",         kicker: "// Never miss a filing",
    desc: "Every MCA · Income-Tax · GST · TDS · Advance-Tax deadline in one view, with T-7 / T-1 email reminders. Colour-coded urgency, filing status pulled from your books.",
    img: "/screenshots/compliance-calendar.jpg" },
  { key: "bom",                 title: "BOM · Assembly · MRP",             kicker: "// Manufacturing",
    desc: "Multi-level Bill of Materials with route steps, batch assembly that consumes raw material and posts finished stock automatically, and MRP that plans procurement to demand.",
    img: "/screenshots/bom.jpg" },
  { key: "job-work",            title: "Job Work + ITC-04",                kicker: "// Operations",
    desc: "Send RM out on returnable challan, receive processed material back, and auto-generate the quarterly ITC-04 with Table 4 / 5A. Full challan audit trail.",
    img: "/screenshots/job-work.jpg" },
  { key: "tds",                 title: "TDS + 26AS Reconcile",             kicker: "// Statutory",
    desc: "92 sections under the new Income-tax Act 2025. Auto-deduction on invoice/bill. FIFO challan mapping, Sec 201(1A) interest auto-calc, and Form 26AS reconciliation in one workspace.",
    img: "/screenshots/tds.jpg" },
];

// ─── Capability cards (bento) ────────────────────────────────────────────────
const CAPABILITIES = [
  { icon: Layers,       title: "Full-stack ERP, one login",           desc: "Accounting · Sales · Purchase · Inventory · Assets · Manufacturing · Dealership · Compliance. No integrations to babysit, no data silos to reconcile.", span: "md:col-span-2" },
  { icon: Landmark,     title: "Dual Depreciation",                   desc: "Book (Sch. II) and Tax (IT Act WDV) registers side-by-side, difference report ready for the auditor.", span: "" },
  { icon: Sparkles,     title: "AI That Explains, Not Executes",       desc: "GPT-5.2 drafts journals, extracts bills, flags anomalies. Every action logged. Nothing material posts silently.", span: "" },
  { icon: FileCheck2,   title: "GST-Native, End-to-End",              desc: "State-of-supply auto-routing, GSTR-1/3B/9, live NIC e-invoice IRN & E-way Bill from the same invoice screen.", span: "md:col-span-2" },
  { icon: GitBranch,    title: "Multi-Branch & Consolidation",         desc: "Branch numbering series, inter-branch vouchers, AS-21 consolidation with IC-elimination.", span: "" },
  { icon: Search,       title: "Cmd-K Everywhere",                     desc: "One search across every voucher, invoice, party, item and account. Deep-link to any drill-down.", span: "" },
];

// 4 headline pillars showcased next to the hero logo
const STATS = [
  { icon: Factory,     v: "Manufacturing",  l: "Fully integrated",     sub: "BOM · MRP · Shopfloor · OEE",             color: "#2563EB" },
  { icon: ShieldCheck, v: "Compliance",      l: "GST · TDS · MCA",       sub: "Live filings, calendar & wizards",         color: "#A855F7" },
  { icon: Sparkles,    v: "AI-Native",       l: "GPT-5.2 built-in",       sub: "Drafts · OCR · Anomaly detection",         color: "#EC4899" },
  { icon: Landmark,    v: "Multi-Entity",    l: "AS-21 Consolidation",    sub: "Any depth of group hierarchy",             color: "#F97316" },
];

// ─── Security & reliability pillars ──────────────────────────────────────────
const SECURITY = [
  { icon: Lock,           t: "HttpOnly Cookie Auth",   d: "Zero JWT-in-localStorage attack surface. Session cookies are HttpOnly + Secure + SameSite=Strict with CSRF double-submit token." },
  { icon: KeyRound,       t: "Role-Based Access",       d: "Owner · Accountant · Auditor · AP Clerk · AR Clerk · Manufacturing · Store · Sales — 15+ granular roles with per-module permissions." },
  { icon: History,        t: "Immutable Audit Trail",   d: "Every voucher edit, post, reversal and login is chain-linked and cryptographically stamped. Nothing can be silently altered." },
  { icon: DatabaseBackup, t: "6-Tier Disaster Recovery", d: "MongoDB PITR (35-day rewind), daily encrypted snapshots, cross-region replica, cold S3 archive at 7 years. Full DR playbook shipped." },
  { icon: Trash2,         t: "Soft Delete + Trash",     d: "Nothing is ever hard-deleted. 30-day recovery window on every record with one-click restore and full audit of who trashed what." },
  { icon: Server,         t: "AES-256 at rest, TLS 1.3", d: "Database and object storage encrypted. All traffic TLS 1.3. Optional Sovereign Stack: dedicated DB + private VPC + 99.9% SLA." },
];

const PLAN_TAGS = { solo: "Try it out", starter: "Small teams", growth: "Most popular", professional: "Mid-market", enterprise: "Full ERP" };
const SHORT_FEATURES = {
  solo:        ["Accounting only", "Sales invoicing", "GST-3B basic", "Bank ledger"],
  starter:     ["Everything in Solo", "Full GST (R1/3B/9)", "P2P + O2C workflows", "Credit/Debit Notes"],
  growth:      ["Everything in Starter", "Inventory + Warehouses", "TDS + 26AS", "E-Invoice + E-Way"],
  professional:["Everything in Growth", "Procurement (PR/PO/GRN)", "Fixed Assets · Dep", "Advanced approvals"],
  enterprise:  ["Everything in Professional", "Tax Audit · ITR · MCA", "Multi-branch · Consolidation", "MIS · Segment MIS · Job Work"],
};
const PRICING_PLANS_FALLBACK = [
  { id: "solo",         name: "Solo",         price_inr: 799,  bundled_users: 2 },
  { id: "starter",      name: "Starter",      price_inr: 1299, bundled_users: 3 },
  { id: "growth",       name: "Growth",       price_inr: 2499, bundled_users: 6, highlighted: true },
  { id: "professional", name: "Professional", price_inr: 4999, bundled_users: 11 },
  { id: "enterprise",   name: "Enterprise",   price_inr: 7999, bundled_users: 16 },
].map((p) => ({ ...p, tag: PLAN_TAGS[p.id] }));

const COMPARISON = [
  { section: "Accounting", rows: [
    ["Chart of Accounts · Vouchers · Ledgers", "y","y","y","y","y"],
    ["Sales invoicing",                        "y","y","y","y","y"],
    ["GST-3B (basic)",                         "y","y","y","y","y"],
    ["Full GST (GSTR-1 · 3B · 9)",             "" ,"y","y","y","y"],
    ["TDS auto-deduction · 26AS",              "" ,"" ,"y","y","y"],
    ["Purchase Bills · Vendor ledger",         "" ,"y","y","y","y"],
    ["Credit & Debit Notes",                   "" ,"y","y","y","y"],
    ["Recurring invoices",                     "" ,"y","y","y","y"],
  ]},
  { section: "Inventory & Procurement", rows: [
    ["Inventory · Warehouses · Stock movements","" ,"" ,"y","y","y"],
    ["E-Invoice (NIC) + E-Way Bill",            "" ,"" ,"y","y","y"],
    ["Auto payment reminders",                  "" ,"" ,"y","y","y"],
    ["Procurement — PR → PO → GRN → Bill",      "" ,"" ,"" ,"y","y"],
    ["Fixed Assets (dual dep · WDV + SLM)",     "" ,"" ,"" ,"y","y"],
    ["Multi-tier approvals · Vendor scorecard", "" ,"" ,"" ,"y","y"],
  ]},
  { section: "Compliance & Reporting", rows: [
    ["P&L · Balance Sheet · Trial Balance",     "y","y","y","y","y"],
    ["Aging · Dimensions · Advanced reports", "" ,"" ,"" ,"y","y"],
    ["Tax Audit (3CA/3CB/3CD)",                 "" ,"" ,"" ,"" ,"y"],
    ["ITR Auto-Prep",                           "" ,"" ,"" ,"" ,"y"],
    ["MCA Wizards (AOC-4, MGT-7, DPT-3)",       "" ,"" ,"" ,"" ,"y"],
    ["Multi-Branch operations · Inter-branch",  "" ,"" ,"" ,"" ,"y"],
    ["Consolidated group reporting",            "" ,"" ,"" ,"" ,"y"],
    ["Segment MIS · Advanced Audit Trail",      "" ,"" ,"" ,"" ,"y"],
    ["Job Work + Gate Pass + ITC-04",           "" ,"" ,"" ,"" ,"y"],
    ["Compliance Calendar",                     "" ,"" ,"" ,"" ,"y"],
  ]},
  { section: "AI & Automation", rows: [
    ["Ask Zenith360 AI (queries/mo)", "100","300","1,000","5,000","25,000"],
    ["Bill OCR extraction",           "" ,"y","y","y","y"],
    ["Brochure translation (Hindi + 10 Indic)","" ,"" ,"y","y","y"],
    ["Priority AI queue",             "" ,"" ,"" ,"" ,"y"],
  ]},
  { section: "Limits & Support", rows: [
    ["Bundled users (extra @ ₹499/user)","1+1","1+2","1+5","1+10","1+15"],
    ["Storage",                          "1 GB","5 GB","25 GB","100 GB","500 GB"],
    ["Emails / month",                   "200","500","2,000","10,000","∞"],
    ["E-mail support",                   "y","y","y","y","y"],
    ["Priority support (4-hr SLA)",      "" ,"" ,"" ,"" ,"y"],
    ["Named Customer Success Manager",   "" ,"" ,"" ,"" ,"y"],
  ]},
];

const ADDONS = [
  { name: "Manufacturing Seat",     price: "₹699/user/mo", desc: "Shopfloor · PPC · MRP · OEE · SPC · BOM · Andon" },
  { name: "Dealership Seat",         price: "₹699/user/mo", desc: "Auto DMS · Van Sales · Beat Plan · On-road Pricing" },
  { name: "Compliance / DSC Seat",   price: "₹499/user/mo", desc: "CA/CS logins · DSC signing · Aadhaar eSign · MCA wizards" },
  { name: "Sovereign Stack",         price: "₹35,000/mo",   desc: "Dedicated DB + Priority Compute + 99.9% SLA + Private VPC" },
];

const inr = (n) => `₹${Math.round(n).toLocaleString("en-IN")}`;
const Tick = ({ v }) =>
  v === "y" ? <CheckCircle2 className="w-4 h-4 text-accent mx-auto" data-testid="cmp-yes" />
    : v ? <span className="text-xs font-mono">{v}</span>
    : <span className="text-muted-foreground/30">—</span>;

// ─────────────────────────────────────────────────────────────────────────────
// Pricing section (unchanged pricing logic)
// ─────────────────────────────────────────────────────────────────────────────
function PricingSection() {
  const [live, setLive] = useState(null);
  const [cycle, setCycle] = useState("monthly");

  useEffect(() => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/plans/public`;
    axios.get(url).then((r) => setLive(r.data)).catch(() => {});
  }, []);

  const plans = live?.plans?.length
    ? live.plans.map((p) => ({ ...p, tag: PLAN_TAGS[p.id] || "", highlighted: p.id === "growth",
                                  features: SHORT_FEATURES[p.id] || [] }))
    : PRICING_PLANS_FALLBACK.map((p) => ({ ...p, features: SHORT_FEATURES[p.id] || [] }));

  const discountPct = live?.annual_discount_pct ?? 15;
  const displayPrice = (p) => cycle === "monthly" ? p.price_inr : p.price_inr * (1 - discountPct / 100);
  const savedPerYear = (p) => Math.round(p.price_inr * 12 * (discountPct / 100));

  return (
    <>
      <div className="flex justify-center mb-10">
        <div className="inline-flex items-center gap-1 rounded-sm border border-border bg-card/40 p-1" data-testid="pricing-cycle-toggle">
          <button onClick={() => setCycle("monthly")} data-testid="cycle-monthly"
            className={`px-4 py-1.5 text-xs font-medium rounded-sm transition-colors ${cycle === "monthly" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}>
            Monthly
          </button>
          <button onClick={() => setCycle("annual")} data-testid="cycle-annual"
            className={`px-4 py-1.5 text-xs font-medium rounded-sm transition-colors flex items-center gap-1.5 ${cycle === "annual" ? "bg-accent text-white" : "text-muted-foreground hover:text-foreground"}`}>
            Annual
            <span className={`text-[9px] font-mono uppercase px-1 py-0.5 rounded-sm ${cycle === "annual" ? "bg-white/20" : "bg-accent/20 text-accent"}`}>Save {discountPct}%</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3 mb-14">
        {plans.map((p) => (
          <div key={p.id} data-testid={`landing-plan-${p.id}`}
            className={`p-5 border rounded-sm flex flex-col ${p.highlighted ? "border-accent ring-1 ring-accent bg-accent/5" : "border-border bg-card/40"}`}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-accent">{p.tag}</div>
            <div className="font-heading font-bold text-xl mt-1">{p.name}</div>
            <div className="mt-2">
              <span className="text-3xl font-bold">{inr(displayPrice(p))}</span>
              <span className="text-xs text-muted-foreground"> /mo</span>
            </div>
            {cycle === "annual"
              ? <div className="text-[10px] font-mono text-accent mt-0.5" data-testid={`saved-${p.id}`}>Save {inr(savedPerYear(p))} / year</div>
              : <div className="text-[10px] font-mono text-transparent select-none">·</div>}
            <div className="text-[10px] font-mono uppercase text-muted-foreground mt-1">Admin + {(p.bundled_users || 1) - 1}</div>
            <ul className="mt-4 space-y-1.5 text-xs flex-1">
              {p.features.map((f) => (
                <li key={f} className="flex gap-1.5">
                  <CheckCircle2 className="w-3 h-3 text-accent shrink-0 mt-0.5" />
                  <span className="text-muted-foreground leading-snug">{f}</span>
                </li>
              ))}
            </ul>
            <Link to="/signup" className="mt-4">
              <Button className={`w-full rounded-sm text-white ${p.highlighted ? "bg-accent hover:bg-accent/90" : "bg-foreground text-background hover:opacity-90"}`} data-testid={`landing-cta-${p.id}`}>
                Start free trial
              </Button>
            </Link>
          </div>
        ))}
      </div>

      <ComparisonTable plans={plans} cycle={cycle} displayPrice={displayPrice} />
      <Estimator plans={plans} addons={live?.addon_seats} cycle={cycle} discountPct={discountPct} />
      <AddonRow addons={live?.addon_seats?.length ? live.addon_seats : null} />
    </>
  );
}

function ComparisonTable({ plans, cycle, displayPrice }) {
  return (
    <div className="border border-border rounded-sm overflow-x-auto bg-card/40" data-testid="landing-comparison">
      <table className="w-full text-sm min-w-[900px]">
        <thead className="bg-muted/30 sticky top-0">
          <tr className="text-[10px] font-mono uppercase text-muted-foreground">
            <th className="text-left p-3 w-[38%]">Feature</th>
            {plans.map((p) => (
              <th key={p.id} className="p-3 text-center border-l border-border">
                <div className="font-bold text-foreground text-[13px] normal-case tracking-normal">{p.name}</div>
                <div className="mt-0.5 font-mono">{inr(displayPrice(p))}<span className="text-muted-foreground text-[9px]">/mo</span></div>
                {cycle === "annual" && <div className="text-[9px] font-mono text-accent mt-0.5">billed annually</div>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {COMPARISON.map((sec) => (
            <React.Fragment key={sec.section}>
              <tr className="bg-muted/20 border-t border-border"><td colSpan={6} className="p-2 text-[10px] font-mono uppercase tracking-widest text-accent">// {sec.section}</td></tr>
              {sec.rows.map(([label, ...vals]) => (
                <tr key={label} className="border-t border-border/60 hover:bg-muted/10">
                  <td className="p-3 text-muted-foreground">{label}</td>
                  {vals.map((v, i) => (<td key={i} className="p-3 text-center border-l border-border/60"><Tick v={v} /></td>))}
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Estimator({ plans, addons, cycle, discountPct }) {
  const [planId, setPlanId] = useState("growth");
  const [extraUsers, setExtraUsers] = useState(0);
  const [mfgSeats, setMfgSeats] = useState(0);
  const [dealerSeats, setDealerSeats] = useState(0);
  const [dscSeats, setDscSeats] = useState(0);

  const plan = plans.find((p) => p.id === planId) || plans[0];
  const findAddon = (id, fb) => (addons || []).find((a) => a.id === id)?.price_inr_per_user ?? fb;
  const mfgPrice = findAddon("manufacturing", 699);
  const dealerPrice = findAddon("dealership", 699);
  const dscPrice = findAddon("compliance_dsc", 499);
  const extraUserPrice = plan.extra_user_inr ?? 499;

  const base = plan.price_inr;
  const extra = extraUsers * extraUserPrice;
  const mfg = mfgSeats * mfgPrice;
  const dealer = dealerSeats * dealerPrice;
  const dsc = dscSeats * dscPrice;
  const subtotal = base + extra + mfg + dealer + dsc;
  const monthly = cycle === "annual" ? subtotal * (1 - discountPct / 100) : subtotal;
  const gst = monthly * 0.18;
  const totalMonthly = monthly + gst;
  const annualDue = cycle === "annual" ? totalMonthly * 12 : null;
  const totalUsers = (plan.bundled_users || 1) + extraUsers + mfgSeats + dealerSeats + dscSeats;

  const Slider = ({ label, value, setValue, max, price, testid, sub }) => (
    <div>
      <div className="flex items-baseline justify-between text-xs">
        <label className="text-muted-foreground">{label} <span className="text-[10px] font-mono">(₹{price.toLocaleString("en-IN")}/user/mo)</span></label>
        <span className="font-mono text-foreground font-bold" data-testid={`${testid}-value`}>{value}</span>
      </div>
      <input type="range" min="0" max={max} step="1" value={value}
        onChange={(e) => setValue(Number(e.target.value))}
        className="w-full accent-accent mt-1.5 cursor-pointer"
        data-testid={testid} />
      {sub && <div className="text-[10px] text-muted-foreground/70 font-mono">{sub}</div>}
    </div>
  );

  return (
    <div className="mt-14" data-testid="landing-estimator">
      <div className="text-center mb-6">
        <div className="text-[10px] font-mono uppercase tracking-widest text-accent">// Estimator</div>
        <h3 className="font-heading font-bold text-2xl md:text-3xl mt-2 flex items-center justify-center gap-2">
          <Calculator className="w-6 h-6 text-accent" /> Build your bill
        </h3>
        <p className="text-sm text-muted-foreground mt-2">Pick a plan, drag the sliders — the price updates live.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 border border-border rounded-sm p-5 bg-card/40">
        <div className="lg:col-span-2 space-y-5">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">1. Pick your base plan</div>
            <div className="flex flex-wrap gap-2">
              {plans.map((p) => (
                <button key={p.id} onClick={() => setPlanId(p.id)} data-testid={`est-plan-${p.id}`}
                  className={`px-3 py-2 rounded-sm text-xs border transition-colors ${planId === p.id ? "bg-accent text-white border-accent" : "border-border text-muted-foreground hover:text-foreground"}`}>
                  <span className="font-medium">{p.name}</span>
                  <span className="ml-1.5 font-mono opacity-80">{inr(p.price_inr)}</span>
                </button>
              ))}
            </div>
            <div className="text-[10px] font-mono text-muted-foreground mt-2">
              Includes <b className="text-foreground">Admin + {(plan.bundled_users || 1) - 1}</b> users bundled
            </div>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-3">2. Add seats</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
              <Slider label="Extra accounting users" value={extraUsers} setValue={setExtraUsers} max={50} price={extraUserPrice} testid="est-extra-users" sub={extraUsers > 0 ? `Beyond the ${plan.bundled_users} bundled seats` : "Slide to add more"} />
              <Slider label="Manufacturing seats"     value={mfgSeats}    setValue={setMfgSeats}    max={30} price={mfgPrice}       testid="est-mfg-seats"    sub="Shopfloor · PPC · MRP · OEE · SPC" />
              <Slider label="Dealership seats"        value={dealerSeats} setValue={setDealerSeats} max={30} price={dealerPrice}    testid="est-dealer-seats" sub="Auto DMS · Van Sales · Beat Plan" />
              <Slider label="Compliance / DSC seats"  value={dscSeats}    setValue={setDscSeats}    max={10} price={dscPrice}       testid="est-dsc-seats"    sub="CA/CS logins · DSC signing" />
            </div>
          </div>
        </div>

        <div className="border border-accent/30 rounded-sm p-4 bg-accent/5 self-start">
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent">Your bill · {cycle}</div>
          <table className="w-full text-xs mt-3">
            <tbody className="divide-y divide-border/60">
              <tr><td className="py-1.5 text-muted-foreground">{plan.name} base</td><td className="py-1.5 text-right font-mono">{inr(base)}</td></tr>
              {extraUsers > 0 && <tr><td className="py-1.5 text-muted-foreground">+ {extraUsers} extra user{extraUsers > 1 ? "s" : ""}</td><td className="py-1.5 text-right font-mono">{inr(extra)}</td></tr>}
              {mfgSeats > 0    && <tr><td className="py-1.5 text-muted-foreground">+ {mfgSeats} Mfg seat{mfgSeats > 1 ? "s" : ""}</td><td className="py-1.5 text-right font-mono">{inr(mfg)}</td></tr>}
              {dealerSeats > 0 && <tr><td className="py-1.5 text-muted-foreground">+ {dealerSeats} Dealership seat{dealerSeats > 1 ? "s" : ""}</td><td className="py-1.5 text-right font-mono">{inr(dealer)}</td></tr>}
              {dscSeats > 0    && <tr><td className="py-1.5 text-muted-foreground">+ {dscSeats} DSC seat{dscSeats > 1 ? "s" : ""}</td><td className="py-1.5 text-right font-mono">{inr(dsc)}</td></tr>}
              <tr><td className="py-1.5 text-muted-foreground">Subtotal</td><td className="py-1.5 text-right font-mono">{inr(subtotal)}</td></tr>
              {cycle === "annual" && <tr><td className="py-1.5 text-accent">Annual discount ({discountPct}%)</td><td className="py-1.5 text-right font-mono text-accent">−{inr(subtotal - monthly)}</td></tr>}
              <tr><td className="py-1.5 text-muted-foreground">GST 18%</td><td className="py-1.5 text-right font-mono">{inr(gst)}</td></tr>
            </tbody>
          </table>
          <div className="mt-3 pt-3 border-t border-accent/30">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Total per month</div>
            <div className="text-3xl font-bold text-accent mt-0.5" data-testid="est-total-monthly">{inr(totalMonthly)}</div>
            {annualDue && (<div className="text-[11px] font-mono text-muted-foreground mt-1" data-testid="est-total-annual">Billed {inr(annualDue)} once a year</div>)}
            <div className="text-[10px] font-mono text-muted-foreground mt-2">{totalUsers} total user{totalUsers > 1 ? "s" : ""} across every seat type</div>
          </div>
          <Link to="/signup"><Button className="w-full mt-4 rounded-sm bg-accent hover:bg-accent/90 text-white text-xs gap-1" data-testid="est-signup">Start free trial <ArrowRight className="w-3 h-3" /></Button></Link>
        </div>
      </div>
    </div>
  );
}

function AddonRow({ addons }) {
  const items = addons?.length
    ? addons.map((a) => ({ name: a.name, price: a.price_inr_per_user
                                ? `₹${a.price_inr_per_user.toLocaleString("en-IN")}/user/mo`
                                : a.price_inr_month
                                    ? `₹${a.price_inr_month.toLocaleString("en-IN")}/mo`
                                    : "", desc: a.description }))
    : ADDONS;
  return (
    <div className="mt-14">
      <div className="text-center mb-6">
        <div className="text-[10px] font-mono uppercase tracking-widest text-accent">// Add-ons</div>
        <h3 className="font-heading font-bold text-2xl md:text-3xl mt-2">Stack these on any plan</h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {items.map((a) => (
          <div key={a.name} className="p-4 border border-border rounded-sm bg-card/40" data-testid={`landing-addon-${a.name.slice(0,4)}`}>
            <div className="font-bold">{a.name}</div>
            <div className="mt-1 font-mono text-accent">{a.price}</div>
            <div className="text-xs text-muted-foreground mt-2">{a.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Product Tour — screenshot carousel
// ─────────────────────────────────────────────────────────────────────────────
function ProductTour() {
  const [active, setActive] = useState(TOUR[0].key);
  const current = TOUR.find((t) => t.key === active) || TOUR[0];
  return (
    <section id="tour" className="border-b border-border bg-gradient-to-b from-background to-card/20">
      <div className="max-w-7xl mx-auto px-6 py-24">
        <div className="mb-10 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-accent mb-3">// Product Tour</div>
            <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tight max-w-2xl">See the modules that make Zenith 360 different.</h2>
          </div>
          <p className="text-muted-foreground max-w-md text-sm">No cherry-picked mockups. These are real screenshots from a live tenant — click any tab to jump.</p>
        </div>

        <div className="grid md:grid-cols-12 gap-6" data-testid="product-tour">
          {/* Tabs (left rail) */}
          <div className="md:col-span-4 space-y-2">
            {TOUR.map((t) => {
              const on = t.key === active;
              return (
                <button key={t.key} onClick={() => setActive(t.key)}
                  data-testid={`tour-tab-${t.key}`}
                  className={`w-full text-left rounded-sm border p-4 transition-all ${on ? "border-accent bg-accent/10 shadow-[0_0_0_1px] shadow-accent" : "border-border hover:border-accent/40 bg-card/40"}`}>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-accent">{t.kicker}</div>
                  <div className={`font-heading font-semibold mt-1 ${on ? "text-lg text-foreground" : "text-base text-foreground/90"}`}>{t.title}</div>
                  {on && <div className="text-sm text-foreground/80 leading-relaxed mt-2">{t.desc}</div>}
                </button>
              );
            })}
          </div>

          {/* Screenshot pane */}
          <motion.div
            key={current.key}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="md:col-span-8 border border-border rounded-sm overflow-hidden bg-card shadow-2xl"
            data-testid="tour-screen">
            <div className="border-b border-border px-4 py-2.5 flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground bg-muted/20">
              <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
              <span className="w-2.5 h-2.5 rounded-full bg-yellow-400/70" />
              <span className="w-2.5 h-2.5 rounded-full bg-green-400/70" />
              <span className="ml-3">zenith360.in / app / {current.key}</span>
            </div>
            <img src={current.img} alt={current.title} className="w-full block" loading="lazy" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Security & Reliability pillar
// ─────────────────────────────────────────────────────────────────────────────
function SecuritySection() {
  return (
    <section id="security" className="border-b border-border relative">
      <div className="max-w-7xl mx-auto px-6 py-24">
        <div className="mb-14 text-center max-w-3xl mx-auto">
          <div className="text-[11px] font-mono uppercase tracking-widest text-accent mb-3">// Security · Backup · Reliability</div>
          <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tight">Your books belong to you. Forever.</h2>
          <p className="mt-5 text-muted-foreground leading-relaxed">
            Finance software is a target. So we built Zenith 360 the way a bank builds core: defence-in-depth,
            append-only trails, and a data-recovery plan you can actually read.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {SECURITY.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.t}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className="border border-border rounded-sm p-6 bg-card hover:border-accent/40 transition-colors"
                data-testid={`sec-${s.t.split(" ")[0].toLowerCase()}`}>
                <Icon className="w-5 h-5 text-accent mb-4" />
                <div className="font-heading font-semibold text-lg mb-2">{s.t}</div>
                <div className="text-base text-foreground/80 leading-relaxed">{s.d}</div>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-8 border border-accent/30 bg-accent/5 rounded-sm p-5 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-6 h-6 text-accent" />
            <div>
              <div className="font-heading font-semibold">Data Residency & Portability</div>
              <div className="text-xs text-muted-foreground">All data hosted in Mumbai region (India). One-click export in Excel, CSV, JSON, and Tally XML — no lock-in.</div>
            </div>
          </div>
          <Link to="/signup"><Button size="sm" className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="sec-cta">Start free <ArrowRight className="w-3.5 h-3.5" /></Button></Link>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────────────────────────────────────
export default function Landing() {
  return (
    <div className="min-h-screen dark bg-background text-foreground overflow-x-hidden">
      {/* ───────── Header ───────── */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/70 border-b border-border">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-3">
          <Link to="/" className="flex items-center gap-3" data-testid="landing-logo">
            <img src="/zenith360-logo.png" alt="Zenith 360 ERP" className="h-11 w-auto" />
            <div className="border-l border-border pl-3 hidden sm:block">
              <div className="font-heading font-bold text-base leading-tight text-logo-gradient">Zenith 360 ERP</div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-foreground/60 leading-tight">India · AI-native · Finance-grade</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-foreground/75">
            <a href="#capabilities" className="hover:text-foreground transition-colors">Capabilities</a>
            <a href="#tour" className="hover:text-foreground transition-colors">Product Tour</a>
            <a href="#security" className="hover:text-foreground transition-colors">Security</a>
            <a href="#pricing" className="hover:text-foreground transition-colors">Pricing</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login"><Button variant="ghost" size="sm" className="rounded-sm" data-testid="landing-login-btn">Sign in</Button></Link>
            <Link to="/signup"><Button size="sm" className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="landing-signup-btn">Start free</Button></Link>
          </div>
        </div>
      </header>

      {/* ───────── Hero ───────── */}
      <section className="relative dark-hero dark-hero-glow border-b border-border noise-overlay overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-24 grid md:grid-cols-12 gap-10 lg:gap-14 items-center relative">
          <div className="md:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/20 rounded-sm mb-6 text-[11px] font-mono uppercase tracking-widest text-white/80 bg-white/5">
              <span className="saffron-dot" /> India-first · AI-native · Finance-grade
            </motion.div>

            {/* Big wordmark — full brand name in logo gradient */}
            <motion.div
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05 }}
              className="mb-4" data-testid="hero-wordmark">
              <div className="font-heading font-black text-6xl sm:text-7xl lg:text-8xl xl:text-9xl leading-[0.9] tracking-tighter text-logo-gradient">
                ZENITH<span className="align-super text-4xl sm:text-5xl lg:text-6xl xl:text-7xl">360</span>
              </div>
              <div className="mt-1 flex items-center gap-3">
                <div className="h-[3px] w-16 bg-logo-gradient rounded-full" />
                <div className="font-heading font-bold text-2xl md:text-3xl tracking-[0.35em] text-white">ERP</div>
              </div>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
              className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-white mt-2">
              The finance & operations OS<br />
              <span className="text-logo-gradient">Indian SMEs finally deserve.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.22 }}
              className="mt-6 text-lg md:text-xl text-white/85 max-w-2xl leading-relaxed">
              One login for accounting, GST, TDS, inventory, procurement, manufacturing,
              dealership, MIS, MCA and Tax Audit — with an AI assistant that <em className="text-white">drafts</em>,
              <em className="text-white"> explains</em> and asks before it posts.
              <span className="block mt-2 font-medium text-white/95">Deeper than Zoho. Easier than SAP. Sharper than Tally.</span>
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}
              className="mt-9 flex flex-wrap items-center gap-3">
              <Link to="/signup">
                <Button size="lg" className="rounded-sm bg-logo-gradient hover:opacity-95 text-white gap-2 h-12 px-7 text-base font-semibold shadow-lg shadow-accent/25 border-0" data-testid="hero-cta-signup">
                  Start free — no card <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <a href="#tour">
                <Button size="lg" variant="outline" className="rounded-sm border-white/30 h-12 px-6 text-base text-white hover:bg-white/10" data-testid="hero-cta-tour">
                  See product tour
                </Button>
              </a>
              <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-white/70 pl-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" /> 14-day trial · No credit card
              </div>
            </motion.div>
          </div>

          {/* Right rail — big logo + 4 pillar cards (Manufacturing / Compliance / AI / Multi-Entity) */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }}
            className="md:col-span-5">
            <div className="relative rounded-sm overflow-hidden border border-white/15 bg-white/[0.03] p-6 backdrop-blur-sm">
              <div className="absolute inset-0 bg-logo-gradient opacity-[0.06] pointer-events-none" />
              <div className="relative flex items-center justify-center">
                <img src="/zenith360-logo.png" alt="Zenith 360 ERP" className="w-full max-w-[380px] h-auto drop-shadow-2xl" data-testid="hero-logo" />
              </div>
              <div className="relative grid grid-cols-2 gap-2.5 mt-4">
                {STATS.map((s) => {
                  const Icon = s.icon;
                  return (
                    <div key={s.l} className="border border-white/15 bg-black/40 backdrop-blur px-3.5 py-3.5 rounded-sm hover:border-white/30 transition-colors" data-testid={`hero-pillar-${s.v.toLowerCase().replace(/\s/g, '-')}`}>
                      <Icon className="w-5 h-5 mb-2" style={{ color: s.color }} />
                      <div className="font-heading font-bold text-base text-white leading-tight">{s.v}</div>
                      <div className="text-[10px] font-mono uppercase tracking-widest mt-1 leading-tight" style={{ color: s.color }}>{s.l}</div>
                      <div className="text-[10px] text-white/70 mt-1 leading-snug">{s.sub}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ───────── Trust strip ───────── */}
      <section className="border-b border-border bg-muted/10">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-wrap items-center justify-around gap-y-3 text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
          <span className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-accent" /> IT Act 2025 · Sec 392–394</span>
          <span className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-accent" /> NIC e-Invoice · Live IRN</span>
          <span className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-accent" /> GSTR-1 / 3B / 9</span>
          <span className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-accent" /> AS-21 Consolidation</span>
          <span className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-accent" /> Tally + Zoho migrator</span>
        </div>
      </section>

      {/* ───────── Capabilities bento ───────── */}
      <section id="capabilities" className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="mb-14 flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-accent mb-3">// Capabilities</div>
              <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tight">Everything a serious finance team needs.</h2>
            </div>
            <p className="text-muted-foreground max-w-md text-sm">Built with GL + subsidiary-ledger principles. Six modules on day one. Zero re-imports, ever.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {CAPABILITIES.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={f.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  className={`border border-border p-6 rounded-sm bg-card hover:border-accent/40 transition-colors ${f.span}`}>
                  <Icon className="w-5 h-5 text-accent mb-4" />
                  <div className="font-heading font-semibold text-lg mb-2">{f.title}</div>
                  <div className="text-base text-foreground/80 leading-relaxed">{f.desc}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ───────── Product Tour ───────── */}
      <ProductTour />

      {/* ───────── AI ───────── */}
      <section id="ai" className="border-b border-border relative">
        <div className="max-w-7xl mx-auto px-6 py-24 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <div className="text-[11px] font-mono uppercase tracking-widest text-accent mb-3">// AI-Native</div>
            <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tight">AI that <em className="text-accent not-italic">explains</em>, not just executes.</h2>
            <p className="mt-6 text-base md:text-lg text-foreground/80 leading-relaxed">
              "Book office rent for April from ICICI with GST and Admin dimension." — get a fully-formed
              journal entry draft with confidence score and reasoning. Every AI action is logged.
              Nothing material is posted without your click.
            </p>
            <ul className="mt-8 space-y-3 text-sm">
              {[
                "Natural-language journal drafting",
                "Document-to-entry extraction from bills (OCR + LLM)",
                "Anomaly detection & GL vs sub-ledger mismatch",
                "Suggested dimensions, tags and GST treatment",
              ].map((x) => (
                <li key={x} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-accent shrink-0 mt-0.5" /><span className="text-muted-foreground">{x}</span></li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-7 border border-border rounded-sm bg-card overflow-hidden">
            <div className="border-b border-border px-4 py-3 flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-muted-foreground">
              <Sparkles className="w-3.5 h-3.5 text-accent" /> AI Assistant · Journal Draft
            </div>
            <div className="p-6 space-y-4">
              <div className="text-xs text-muted-foreground italic">→ "Book office rent for April ₹45,000 from ICICI with GST and Admin"</div>
              <div className="border border-accent/30 bg-accent/5 rounded-sm p-4 space-y-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-accent">Draft — confidence 94%</div>
                <table className="w-full text-xs tabular-nums">
                  <thead>
                    <tr className="text-[10px] uppercase text-muted-foreground border-b border-border">
                      <th className="text-left py-2">Account</th><th className="text-right">Debit</th><th className="text-right">Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-border/60"><td className="py-2 font-mono">5200 Rent Expense</td><td className="text-right">₹38,135.59</td><td className="text-right">—</td></tr>
                    <tr className="border-b border-border/60"><td className="py-2 font-mono">1400 Input CGST</td><td className="text-right">₹3,432.20</td><td className="text-right">—</td></tr>
                    <tr className="border-b border-border/60"><td className="py-2 font-mono">1410 Input SGST</td><td className="text-right">₹3,432.20</td><td className="text-right">—</td></tr>
                    <tr><td className="py-2 font-mono">1010 Bank - ICICI</td><td className="text-right">—</td><td className="text-right">₹45,000.00</td></tr>
                  </tbody>
                </table>
                <div className="text-[10px] uppercase font-mono text-warning">User confirmation required — nothing posted yet.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────── Security ───────── */}
      <SecuritySection />

      {/* ───────── Compliance ───────── */}
      <section id="compliance" className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="mb-14 max-w-2xl">
            <div className="text-[11px] font-mono uppercase tracking-widest text-accent mb-3">// Statutory Compliance</div>
            <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tight">Every filing India throws at you.</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-4">
            {[
              { icon: ScrollText,    t: "GST",           d: "GSTR-1 · 3B · 9 · CMP-08. State-of-supply auto-routing, RCM handling, e-invoice IRN, E-way Bill." },
              { icon: BadgeIndianRupee,t: "Direct Tax",   d: "TDS/TCS (92 sections) · 26AS reconcile · Advance Tax · MAT · Tax Audit 3CA/3CB/3CD · ITR auto-prep." },
              { icon: Building2,     t: "MCA & ROC",     d: "AOC-4 · MGT-7 · MGT-7A · DPT-3 · MSME-1 · Form 8 · DIR-3 KYC wizards with pre-filled XBRL." },
              { icon: CalendarClock, t: "Deadlines",     d: "One statutory calendar for every filing with T-7 / T-1 reminder emails to the right roles." },
            ].map((x) => {
              const Icon = x.icon;
              return (
                <div key={x.t} className="border border-border rounded-sm p-6 bg-card hover:border-accent/40 transition-colors">
                  <Icon className="w-6 h-6 text-accent mb-4" />
                  <div className="font-heading text-lg font-semibold mb-2">{x.t}</div>
                  <div className="text-base text-foreground/80 leading-relaxed">{x.d}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ───────── Verticals ───────── */}
      <section className="border-b border-border bg-muted/10">
        <div className="max-w-7xl mx-auto px-6 py-24 grid md:grid-cols-3 gap-6">
          {[
            { icon: Factory, t: "Manufacturing", d: "BOM · Work-orders · MRP · Shopfloor · OEE · SPC · Quality · Costing. Job Work + ITC-04 built in." },
            { icon: Wrench,  t: "Dealership",     d: "Auto DMS · Van Sales · Beat Plans · On-road Pricing · Service Vouchers. Multi-brand, multi-branch." },
            { icon: ShieldCheck, t: "Compliance / CA", d: "CA-CS logins · DSC signing · Aadhaar eSign · MCA wizards · Tax audit workspace with remarks." },
          ].map((v) => {
            const Icon = v.icon;
            return (
              <div key={v.t} className="border border-border rounded-sm p-8 bg-background">
                <Icon className="w-7 h-7 text-accent mb-5" />
                <div className="font-heading text-xl font-semibold mb-2">{v.t}</div>
                <div className="text-base text-foreground/80 leading-relaxed">{v.d}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ───────── Pricing ───────── */}
      <section id="pricing" className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <div className="text-[10px] font-mono uppercase tracking-widest text-accent">// Pricing</div>
            <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tight mt-3">One platform. Five plans.</h2>
            <p className="mt-4 text-base text-foreground/80">
              Progressive tiers by module — start small, unlock more as you grow. No user caps; extra seats at ₹499/user/month.
              Prices exclusive of 18% GST.
            </p>
          </div>
          <PricingSection />
        </div>
      </section>

      {/* ───────── CTA ───────── */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 py-24 relative z-10">
          <div className="border border-border p-14 rounded-sm bg-card/40 backdrop-blur-xl text-center">
            <img src="/zenith360-logo.png" alt="Zenith 360" className="h-16 mx-auto mb-6 opacity-90" />
            <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tight">Stop wrestling with your books.</h2>
            <p className="mt-4 text-base text-foreground/80 max-w-xl mx-auto">Start with a demo company pre-loaded with an India Chart of Accounts, branches, dimensions and sample data. Migrate from Tally or Zoho in a single upload.</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/signup"><Button size="lg" className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-2 h-12 px-6" data-testid="cta-signup">Start free — no card <ArrowRight className="w-4 h-4" /></Button></Link>
              <Link to="/login"><Button size="lg" variant="outline" className="rounded-sm border-border h-12 px-6" data-testid="cta-login">Sign in</Button></Link>
            </div>
          </div>
        </div>
      </section>

      {/* ───────── Footer ───────── */}
      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-wrap items-center justify-between text-xs text-muted-foreground gap-4">
          <div className="flex items-center gap-3">
            <img src="/zenith360-logo.png" alt="Zenith 360" className="h-7" />
            <span>© 2026 Zenith 360 ERP · Built for Indian SMEs</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2"><Languages className="w-3.5 h-3.5" /> English · हिन्दी · 10 Indic languages</div>
            <Link to="/admin/login" data-testid="landing-staff-link"
                  className="text-[10px] font-mono uppercase tracking-widest text-foreground/40 hover:text-accent transition-colors">
              Staff sign-in
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
